import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, Clock, Users, MapPin, Lock, Megaphone, Info, Dices, Trophy, Coins, Sparkles, Mic } from "lucide-react";
import { Nav, Footer } from "@/components/Nav";
import { Countdown } from "@/components/Countdown";
import { TableSection } from "@/components/TableSection";
import { GuestList } from "@/components/GuestList";
import { ShareButton } from "@/components/ShareButton";
import { VideoCarousel } from "@/components/VideoCarousel";
import { CountUp } from "@/components/CountUp";
import { useEvent, useAnnouncements } from "@/hooks/useEvent";
import { IMAGES, KARAOKE_CLIPS } from "@/lib/constants";
import { directionsLink } from "@/lib/calendar";

const fade = { hidden: { opacity: 0, y: 24 }, show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }) };

function InfoCard({ icon: Icon, label, value, testid, children }) {
  return (
    <div className="rounded-3xl bg-white border border-hair shadow-soft p-6 sm:p-7 hover:shadow-lift transition-all" data-testid={testid}>
      <div className="w-11 h-11 rounded-full bg-goldLight grid place-items-center text-gold mb-4">
        <Icon size={20} />
      </div>
      <p className="text-eyebrow text-inkMuted">{label}</p>
      <p className="font-serif text-xl sm:text-2xl text-espresso mt-1">{value}</p>
      {children}
    </div>
  );
}

export default function Home() {
  const nav = useNavigate();
  const { data: event } = useEvent();
  const { data: announcements } = useAnnouncements();
  const latest = announcements?.[0];

  const dateLabel = "November 14, 2026";

  return (
    <div className="min-h-screen bg-cream">
      <Nav />

      {/* HERO */}
      <section className="relative overflow-hidden grain">
        <div className="absolute inset-0">
          <img src={IMAGES.hero} alt="Friendsgiving tablescape" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-espressoDeep/70 via-espressoDeep/55 to-espressoDeep/85" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 pt-16 pb-20 sm:pt-24 sm:pb-28 text-center">
          <motion.p variants={fade} initial="hidden" animate="show" custom={0} className="text-eyebrow text-goldLight">
            You're Invited · A Private Gathering
          </motion.p>
          <motion.h1 variants={fade} initial="hidden" animate="show" custom={1} className="mt-4 font-serif text-white leading-[0.95]">
            <span className="block text-2xl sm:text-3xl tracking-[0.2em] text-goldLight/90 font-normal">MIKE &amp; SAM'S</span>
            <span className="block text-6xl sm:text-8xl mt-2 italic">Friendsgiving</span>
          </motion.h1>
          <motion.p variants={fade} initial="hidden" animate="show" custom={2} className="mt-6 text-white/80 text-lg">
            {dateLabel} &nbsp;·&nbsp; 2:00 PM EST
          </motion.p>

          <motion.div variants={fade} initial="hidden" animate="show" custom={3} className="mt-10">
            <p className="text-eyebrow text-white/60 mb-5">Friendsgiving Starts In</p>
            <Countdown target={event?.event_datetime_iso || "2026-11-14T14:00:00-05:00"} light />
          </motion.div>

          <motion.div variants={fade} initial="hidden" animate="show" custom={4} className="mt-12 flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => nav("/rsvp")} className="gold-gradient text-espressoDeep font-bold text-lg px-10 py-4 rounded-full shadow-gold hover:brightness-105 active:scale-95 transition" data-testid="hero-rsvp-now">
              RSVP Now
            </button>
            <button onClick={() => nav("/my-rsvp")} className="border border-white/30 text-white font-semibold text-lg px-10 py-4 rounded-full backdrop-blur hover:bg-white/10 active:scale-95 transition" data-testid="hero-my-rsvp">
              My RSVP
            </button>
          </motion.div>
          {event?.total_attending > 0 && (
            <motion.p variants={fade} initial="hidden" animate="show" custom={5} className="mt-8 text-white/70 text-sm">
              <CountUp value={event.total_attending} className="text-goldLight font-semibold" /> friends are already coming
            </motion.p>
          )}
        </div>
      </section>

      {/* ANNOUNCEMENT */}
      {latest && (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 -mt-8 relative z-20">
          <div className="rounded-3xl gold-gradient p-[1.5px] shadow-gold" data-testid="home-announcement">
            <div className="rounded-3xl bg-espressoDeep px-6 py-5 flex items-start gap-4">
              <Megaphone className="text-goldLight shrink-0 mt-1" size={22} />
              <div>
                <p className="text-eyebrow text-goldLight/70">From Mike &amp; Sam</p>
                <p className="text-white font-serif text-lg mt-1">{latest.title}</p>
                <p className="text-white/70 text-sm mt-1">{latest.message}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* EVENT INFO */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          <InfoCard icon={Calendar} label="Date" value={dateLabel} testid="info-date" />
          <InfoCard icon={Clock} label="Time" value="2:00 PM EST" testid="info-time" />
          <InfoCard icon={Users} label="Hosted By" value="Mike & Sam" testid="info-hosts" />
          <InfoCard icon={event?.address ? MapPin : Lock} label="Location" value={event?.address ? "Westlake, FL" : "Revealed after RSVP"} testid="info-location">
            {event?.address ? (
              <div className="mt-2">
                <p className="text-inkSoft text-sm">{event.address}</p>
                <a href={directionsLink(event.address)} target="_blank" rel="noreferrer" className="inline-block mt-3 text-sm font-semibold text-gold hover:underline" data-testid="home-directions">
                  Get Directions →
                </a>
              </div>
            ) : (
              <p className="text-inkSoft text-sm mt-2">RSVP to unlock the exact address.</p>
            )}
          </InfoCard>
        </div>
      </section>

      {/* GALLERY STRIP */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-4">
        <div className="grid grid-cols-3 gap-3 sm:gap-5">
          {[IMAGES.table1, IMAGES.feast, IMAGES.toast].map((src, i) => (
            <div key={i} className={`overflow-hidden rounded-3xl ${i === 1 ? "row-span-1" : ""} aspect-[3/4] sm:aspect-square`}>
              <img src={src} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
            </div>
          ))}
        </div>
      </section>

      <TableSection enabled={!!event?.dish_list_visible} />
      <GuestList enabled={!!event?.guest_list_visible} />

      {/* GOOD TO KNOW */}
      {event?.good_to_know?.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <div className="text-center mb-10">
            <p className="text-eyebrow text-gold">Details</p>
            <h2 className="font-serif text-3xl sm:text-4xl text-espresso mt-2">Good to Know</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {event.good_to_know.map((g, i) => (
              <div key={i} className="rounded-3xl bg-cream2 border border-hair p-6" data-testid={`gtk-${i}`}>
                <div className="flex items-center gap-2 text-gold mb-2"><Info size={16} /><p className="text-eyebrow text-inkMuted">{g.label}</p></div>
                <p className="text-ink">{g.value}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* FUN & GAMES */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
        <div className="text-center mb-10">
          <p className="text-eyebrow text-gold">Before &amp; After Dinner</p>
          <h2 className="font-serif text-3xl sm:text-4xl text-espresso mt-2">Fun &amp; Games</h2>
          <p className="text-inkSoft mt-3 max-w-xl mx-auto">Friendly competition before we eat and after the plates are cleared. Looking to win some mula? Bring <span className="font-semibold text-espresso">$3</span> for Left Right Center — and stay tuned, plenty more games to come.</p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { icon: Dices, name: "Friendsgiving Bingo", tag: "Everyone Plays" },
            { icon: Trophy, name: "Family Feud", tag: "Teams" },
            { icon: Coins, name: "Left Right Center", tag: "$3 Buy-In" },
            { icon: Mic, name: "Karaoke", tag: "Grab the Mic" },
          ].map((g, i) => (
            <div key={i} className="group rounded-3xl bg-espressoDeep grain relative overflow-hidden p-7 text-center hover:-translate-y-1 transition-all shadow-soft hover:shadow-lift" data-testid={`game-${i}`}>
              <div className="relative z-10">
                <div className="w-14 h-14 rounded-2xl bg-white/10 grid place-items-center text-goldLight mx-auto mb-5 group-hover:scale-110 transition-transform">
                  <g.icon size={26} />
                </div>
                <h3 className="font-serif text-2xl text-white">{g.name}</h3>
                <span className="inline-flex items-center gap-1.5 mt-4 text-eyebrow text-goldLight/80"><Sparkles size={13} /> {g.tag}</span>
              </div>
            </div>
          ))}
        </div>

        {/* KARAOKE CAROUSEL */}
        <div className="mt-14 grid lg:grid-cols-2 gap-10 items-center">
          <div className="order-2 lg:order-1 text-center lg:text-left">
            <div className="inline-flex items-center gap-2 text-gold mb-3"><Mic size={20} /><span className="text-eyebrow">Karaoke Night</span></div>
            <h3 className="font-serif text-3xl sm:text-4xl text-espresso">Grab the Mic 🎤</h3>
            <p className="text-inkSoft mt-3 max-w-md mx-auto lg:mx-0">Warm up those vocal cords — the karaoke machine is fired up all night. Here's a taste of the energy you're walking into.</p>
          </div>
          <div className="order-1 lg:order-2">
            <VideoCarousel videos={KARAOKE_CLIPS} />
          </div>
        </div>
      </section>

      {/* SHARE */}
      {event?.sharing_enabled && (
        <section className="max-w-3xl mx-auto px-4 sm:px-6 pb-16 text-center">
          <div className="rounded-[2rem] bg-white border border-hair shadow-soft p-10">
            <h2 className="font-serif text-3xl text-espresso">Spread the Word</h2>
            <p className="text-inkSoft mt-2 mb-6">Know someone who'd love to join? Send them the invite.</p>
            <div className="flex justify-center"><ShareButton /></div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
